import { Vehicle } from 'further-types/lp/vehicle';
import {
  Controller,
  UseFieldArrayReplace,
  useFormContext,
} from 'react-hook-form';
import { realisation as rules } from 'further-ui/validations';
import VehicleSelector from 'components/VehicleSelector';
import FieldRow from 'components/FormElements/FieldRow';
import AppSelectBox from 'components/FormElements/AppSelectBox';
import { AppDatePicker } from 'components/FormElements';
import { CalendarDay } from 'further-ui/utils';
import { useDisclosure } from 'further-ui/hooks';
import { useState } from 'react';
import DataLossWarningModal from 'components/DataLossWarningModal';
import { defaultHoldingClassRealisation } from '../FormWrapper';
import useEqualisations from 'hooks/lp/data/equalisations/useEqualisations';
import { useLayoutContext } from 'contexts/LayoutContext';

type RealisationFormFieldsProps = {
  selectedVehicle: Vehicle;
  assetOptions: Array<{ id: string; title: string }>;
  hasHoldingClassesAddedOnDeal: boolean;
  replace: UseFieldArrayReplace<rules.CreateRealisationFormValues>;
};

const RealisationFormFields: React.FC<RealisationFormFieldsProps> = ({
  selectedVehicle,
  assetOptions,
  hasHoldingClassesAddedOnDeal,
  replace,
}) => {
  const { control, getValues, setValue } =
    useFormContext<rules.CreateRealisationFormValues>();
  const { selectedVehicleId } = useLayoutContext();
  const { latestApprovedEqualisationDate } = useEqualisations(
    selectedVehicleId ?? '',
  );

  const removeModalDisclosure = useDisclosure();
  const [previousAssetId, setPreviousAssetId] = useState<string>('');

  const onAssetChange = () => {
    if (getValues('assetId') !== previousAssetId) {
      replace([defaultHoldingClassRealisation]);
    }
  };

  const setAssetName = () => {
    const assetName = assetOptions.find(
      (asset) => asset.id === getValues('assetId'),
    )?.title;
    setValue('assetName', assetName ?? '');
  };

  const handleAssetChange = () => {
    if (hasHoldingClassesAddedOnDeal) {
      setPreviousAssetId(getValues('assetId') ?? '');
      removeModalDisclosure.onOpen();
    } else {
      onAssetChange();
    }
  };

  const handleConfirmAssetChange = () => {
    onAssetChange();
    removeModalDisclosure.onClose();
  };

  const handleCancelAssetChange = () => {
    setValue('assetId', previousAssetId, {
      shouldDirty: true,
    });

    removeModalDisclosure.onClose();
  };

  return (
    <>
      <FieldRow title="Vehicle" centerTitle>
        <VehicleSelector selectedVehicle={selectedVehicle} control={control} />
      </FieldRow>
      <FieldRow title="Asset" centerTitle>
        <Controller
          name="assetId"
          control={control}
          render={({
            field: { ref, value, ...rest },
            fieldState: { error },
          }) => (
            <AppSelectBox
              {...rest}
              value={value}
              onChange={(event) => {
                handleAssetChange();
                rest.onChange(event);
                setAssetName();
              }}
              fullWidth
              data={assetOptions}
              placeholder="Select asset"
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
      </FieldRow>
      <FieldRow
        title="Realisation date"
        tooltipText="Select a target date if the actual realisation date is unknown at the point of processing. This transaction can be edited after submission to include the actual completion date."
        centerTitle
      >
        <Controller
          name="realisationDate"
          control={control}
          render={({
            fieldState: { error },
            field: { ref, value, onChange, ...rest },
          }) => (
            <AppDatePicker
              error={!!error}
              helperText={error?.message?.toString()}
              value={value?.toUTCDate()}
              onChange={(date, context) => {
                if (date && !context.validationError) {
                  onChange(new CalendarDay(date));
                } else {
                  onChange(null);
                }
              }}
              minDate={latestApprovedEqualisationDate}
              {...rest}
            />
          )}
        />
      </FieldRow>
      <DataLossWarningModal
        disclosure={removeModalDisclosure}
        onConfirm={handleConfirmAssetChange}
        onCancel={handleCancelAssetChange}
        message="Changing the asset will remove all holding classes including any new ones you've added to the deal. Do you want to continue?"
      />
    </>
  );
};

export default RealisationFormFields;
